import { z } from 'zod';

export const PERIODES_STATS = ['30j', '90j', '12m', 'tout'] as const;
export const periodeStatsSchema = z.enum(PERIODES_STATS);
export type PeriodeStats = z.infer<typeof periodeStatsSchema>;

export const LIBELLES_PERIODE: Record<PeriodeStats, string> = {
  '30j': '30 jours',
  '90j': '90 jours',
  '12m': '12 mois',
  tout: 'Depuis le début',
};

/** Début de la période, sur la date de réception des candidatures ; `null` = tout. */
export function debutPeriode(periode: PeriodeStats, maintenant: Date = new Date()): Date | null {
  const debut = new Date(maintenant);
  if (periode === '30j') debut.setDate(debut.getDate() - 30);
  else if (periode === '90j') debut.setDate(debut.getDate() - 90);
  else if (periode === '12m') debut.setMonth(debut.getMonth() - 12);
  else return null;
  return debut;
}

/**
 * Étapes de l'entonnoir (docs/ROADMAP.md point 22). Une candidature compte
 * dans toutes les étapes jusqu'à la plus avancée qu'elle a atteinte, prouvée
 * par son statut ou par une trace (appel joint, rendez-vous, évaluation,
 * rapport signé, fiche employé) : un dossier écarté après l'entretien reste
 * compté dans les entretiens.
 */
export const ETAPES_ENTONNOIR = [
  'recues',
  'contactees',
  'rendezVous',
  'entretiens',
  'rapports',
  'justificatifs',
  'embauches',
] as const;
export type EtapeEntonnoir = (typeof ETAPES_ENTONNOIR)[number];

export const LIBELLES_ETAPE: Record<EtapeEntonnoir, string> = {
  recues: 'Reçues',
  contactees: 'Jointes',
  rendezVous: 'Rendez-vous',
  entretiens: 'Entretiens',
  rapports: 'Rapports signés',
  justificatifs: 'Justificatifs reçus',
  embauches: 'Embauchées',
};

export interface RendementSource {
  source: string;
  recues: number;
  contactees: number;
  entretiens: number;
  embauches: number;
  noteMoyenne: number | null;
}

export interface StatsRecrutement {
  periode: PeriodeStats;
  debut: string | null;
  annonce: { id: string; reference: string | null; intitule: string } | null;
  entonnoir: Record<EtapeEntonnoir, number>;
  sorties: { ecarte: number; desistement: number; vivier: number };
  /** Délai entre la réception et la première tentative d'appel. */
  premierContact: {
    avecContact: number;
    sansContact: number;
    medianeHeures: number | null;
    dansLeDelai: number;
    delaiHeures: number;
  };
  /** Appels téléphoniques clôturés. */
  joignabilite: { appels: number; joints: number; candidatsAppeles: number; candidatsJoints: number };
  sources: RendementSource[];
  /**
   * Pastille du dernier appel précédant le premier entretien, face à la note
   * de cet entretien ; les entretiens éliminatoires (note forcée à 0) sont exclus.
   */
  ecartPastilleNote: {
    paires: number;
    ecartMoyen: number | null;
    parPastille: Array<{ pastille: number; nombre: number; noteMoyenne: number }>;
  };
}

export function taux(numerateur: number, denominateur: number): number | null {
  return denominateur > 0 ? numerateur / denominateur : null;
}

export function pourcentage(valeur: number | null): string {
  return valeur === null ? '—' : `${Math.round(valeur * 100)}\u202f%`;
}

export function dureeLisible(heures: number | null): string {
  if (heures === null) return '—';
  if (heures < 1) return 'moins d’une heure';
  if (heures < 48) return `${Math.round(heures)}\u202fh`;
  return `${(heures / 24).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}\u202fj`;
}

/**
 * Chiffres de la barre de statut (docs/UI.md § Barre de statut) : ce que
 * n'importe quel rôle peut voir d'un coup d'œil, sans donnée nominative.
 * `depuis` est l'avant-dernière connexion du compte — vide à la première
 * connexion, auquel cas il n'y a pas de « nouveautés » à annoncer.
 */
export interface ApercuEnTete {
  /** Annonces qui reçoivent des candidatures : validées ou publiées. */
  annoncesOuvertes: number;
  /** Personnes non anonymisées qui portent au moins une candidature. */
  candidats: number;
  /** Candidatures reçues depuis `depuis` ; 0 quand la date manque. */
  nouvellesCandidatures: number;
  depuis: string | null;
}
